/**
 * Simonara Coach — thin first slice (iter 143).
 *
 * Three free AI tools for talent users. Each tool ends with a deep-link CTA
 * back to the Talent Passport so the coach is a funnel into profile
 * completion, not a standalone product.
 *
 *   /professional/coach-tools?tool=resume    → Resume feedback
 *   /professional/coach-tools?tool=interview → Interview practice
 *   /professional/coach-tools?tool=salary    → Salary negotiation tips
 *
 * Notes on data handling:
 *   - Pasted resume text is sent to the backend for analysis, but the
 *     server does NOT persist it. The client only holds it in state for
 *     the session.
 *   - Interview practice keeps the transcript in client state; each turn
 *     posts the full transcript so the backend stays stateless.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import {
    FileText, MessageSquare, DollarSign, Sparkles, Loader2, ArrowRight,
    CheckCircle2, RotateCcw, Send, Lightbulb, ExternalLink, X,
    Mic, MicOff, Play, Square,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { toast } from "@/components/ui/sonner";
import api, { formatApiError } from "@/lib/api";

const TOOLS = [
    { key: "resume", label: "Resume feedback", icon: FileText, desc: "Paste your resume — get plain-language feedback." },
    { key: "interview", label: "Interview practice", icon: MessageSquare, desc: "Practice one question at a time with feedback." },
    { key: "salary", label: "Salary negotiation", icon: DollarSign, desc: "Framework, scripts, and research sources." },
];

const CoachCTA = ({ cta }) => (
    <Link
        to={cta.to}
        className="mt-5 inline-flex items-center gap-2 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-2.5 text-sm font-semibold"
        data-testid="coach-cta"
    >
        <Sparkles size={14} /> {cta.label} <ArrowRight size={14} />
    </Link>
);

/** Small pill shown when the coach auto-uses the user's Passport role. */
const TailoredBadge = ({ role }) => (
    <span
        className="inline-flex items-center gap-1 rounded-full border border-emerald-500/40 bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider"
        data-testid="coach-tailored-badge"
        title="Prefilled from your Talent Passport"
    >
        <Sparkles size={10} /> Tailored from your Passport · {role}
    </span>
);

/** Hook: fetch the user's Passport-derived coach context once and share it. */
const useCoachContext = () => {
    const [ctx, setCtx] = useState(null);
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const { data } = await api.get("/professional/coach/context");
                if (!cancelled) setCtx(data);
            } catch { if (!cancelled) setCtx({ target_role: "", skills: [], has_context: false }); }
        })();
        return () => { cancelled = true; };
    }, []);
    return ctx;
};

/** Voice playback for a previous interview turn (iter 146).
 *  Renders a <audio> element from the in-memory blob URL and shows the
 *  transcript with filler words visually highlighted so patterns are
 *  obvious both to hear and to read. Audio stays in client memory — no
 *  server persistence.
 */
const FILLER_WORDS = ["um", "uh", "like", "you know", "sort of", "kind of", "basically", "literally", "actually", "so yeah", "right"];

const VoicePlayback = ({ blob, transcript, feedback }) => {
    const [url, setUrl] = useState(null);
    const audioRef = useRef(null);

    useEffect(() => {
        if (!blob) return;
        const u = URL.createObjectURL(blob);
        setUrl(u);
        return () => URL.revokeObjectURL(u);
    }, [blob]);

    if (!blob || !url) return null;

    // Highlight fillers in the transcript. Case-insensitive whole-word match.
    const highlighted = () => {
        if (!transcript) return null;
        // Escape regex specials and build one alternation across all fillers.
        const pattern = FILLER_WORDS.map((w) => w.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")).join("|");
        const rgx = new RegExp(`\\b(${pattern})\\b`, "gi");
        const parts = [];
        let last = 0;
        let match;
        let count = 0;
        while ((match = rgx.exec(transcript)) !== null) {
            if (match.index > last) parts.push({ text: transcript.slice(last, match.index), filler: false });
            parts.push({ text: match[0], filler: true });
            count += 1;
            last = match.index + match[0].length;
        }
        if (last < transcript.length) parts.push({ text: transcript.slice(last), filler: false });
        return { parts, count };
    };

    const rendered = highlighted();
    return (
        <div className="mt-3 rounded-xl border border-emerald-500/25 bg-emerald-500/5 p-3" data-testid="coach-voice-playback">
            <div className="flex items-center gap-2 flex-wrap">
                <audio
                    ref={audioRef}
                    src={url}
                    controls
                    className="max-w-full h-8 rounded"
                    data-testid="coach-voice-audio"
                />
                {rendered && rendered.count > 0 && (
                    <span className="text-[11px] text-amber-700 dark:text-amber-400" data-testid="coach-voice-filler-count">
                        {rendered.count} filler word{rendered.count === 1 ? "" : "s"} detected
                    </span>
                )}
            </div>
            {rendered && (
                <p className="mt-2 text-sm text-foreground leading-relaxed" data-testid="coach-voice-transcript">
                    {rendered.parts.map((p, i) => (
                        p.filler
                            ? <mark key={i} className="rounded px-1 bg-amber-500/25 text-amber-900 dark:text-amber-200" data-testid="coach-voice-filler">{p.text}</mark>
                            : <span key={i}>{p.text}</span>
                    ))}
                </p>
            )}
            {feedback && (
                <div className="mt-2 text-xs text-muted-foreground italic">
                    Coach note: {feedback}
                </div>
            )}
        </div>
    );
};

/** Mic recorder — captures up to 90 seconds of audio and hands the blob
 *  to the caller. Uses MediaRecorder API; falls back cleanly when the
 *  browser doesn't grant mic access. Audio is never persisted server-side. */
const MicRecorder = ({ onTranscript, disabled }) => {
    const [supported, setSupported] = useState(true);
    const [recording, setRecording] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [elapsed, setElapsed] = useState(0);
    const [errMsg, setErrMsg] = useState("");
    const chunksRef = useRef([]);
    const recRef = useRef(null);
    const streamRef = useRef(null);
    const timerRef = useRef(null);

    const MAX_SECONDS = 90;

    useEffect(() => {
        setSupported(typeof window !== "undefined" && !!(navigator?.mediaDevices?.getUserMedia) && !!window.MediaRecorder);
        return () => {
            if (timerRef.current) clearInterval(timerRef.current);
            if (recRef.current && recRef.current.state !== "inactive") recRef.current.stop();
            if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop());
        };
    }, []);

    const stop = useCallback(() => {
        if (recRef.current && recRef.current.state !== "inactive") recRef.current.stop();
        if (timerRef.current) clearInterval(timerRef.current);
        setRecording(false);
    }, []);

    const start = async () => {
        setErrMsg("");
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            streamRef.current = stream;
            // Prefer opus/webm — Whisper accepts webm directly.
            let mime = "audio/webm;codecs=opus";
            if (!window.MediaRecorder.isTypeSupported(mime)) mime = "audio/webm";
            if (!window.MediaRecorder.isTypeSupported(mime)) mime = "";
            const rec = mime ? new MediaRecorder(stream, { mimeType: mime }) : new MediaRecorder(stream);
            recRef.current = rec;
            chunksRef.current = [];
            rec.ondataavailable = (e) => { if (e.data && e.data.size > 0) chunksRef.current.push(e.data); };
            rec.onstop = async () => {
                stream.getTracks().forEach((t) => t.stop());
                streamRef.current = null;
                const blob = new Blob(chunksRef.current, { type: rec.mimeType || "audio/webm" });
                if (!blob.size) { setErrMsg("Nothing was recorded — try again."); return; }
                setUploading(true);
                try {
                    const form = new FormData();
                    form.append("audio", blob, "answer.webm");
                    const { data } = await api.post("/professional/coach/transcribe", form, {
                        headers: { "Content-Type": "multipart/form-data" },
                    });
                    if (data.text) {
                        // iter 146 — hand the blob back too so the caller
                        // can offer replay. The blob is kept in client
                        // memory only; the server never persists audio.
                        onTranscript(data.text, blob);
                    } else {
                        setErrMsg(data.note || "No speech detected — try again.");
                    }
                } catch (err) {
                    setErrMsg(formatApiError(err));
                } finally {
                    setUploading(false);
                }
            };
            rec.start();
            setRecording(true);
            setElapsed(0);
            timerRef.current = setInterval(() => {
                setElapsed((s) => {
                    const next = s + 1;
                    if (next >= MAX_SECONDS) { stop(); }
                    return next;
                });
            }, 1000);
        } catch (err) {
            setErrMsg("Mic access denied — you can still type your answer.");
        }
    };

    if (!supported) return null;

    return (
        <div className="flex items-center gap-2 flex-wrap" data-testid="coach-mic">
            {!recording && !uploading && (
                <button type="button" onClick={start} disabled={disabled}
                    data-testid="coach-mic-start"
                    className="inline-flex items-center gap-1.5 rounded-full border border-emerald-500/40 bg-emerald-500/10 hover:bg-emerald-500/20 px-3 py-1.5 text-xs font-semibold text-emerald-700 dark:text-emerald-400 disabled:opacity-60">
                    <Mic size={12} /> Record answer
                </button>
            )}
            {recording && (
                <button type="button" onClick={stop}
                    data-testid="coach-mic-stop"
                    className="inline-flex items-center gap-1.5 rounded-full border border-rose-500/40 bg-rose-500/10 hover:bg-rose-500/20 px-3 py-1.5 text-xs font-semibold text-rose-700 dark:text-rose-400">
                    <Square size={11} /> Stop ({MAX_SECONDS - elapsed}s left)
                </button>
            )}
            {uploading && (
                <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground" data-testid="coach-mic-uploading">
                    <Loader2 size={12} className="animate-spin" /> Transcribing…
                </span>
            )}
            {errMsg && !recording && !uploading && (
                <span className="text-xs text-rose-600" data-testid="coach-mic-err">{errMsg}</span>
            )}
        </div>
    );
};

/** Modal: before/after diff for the Import Bridge. */
const ImportDiffModal = ({ open, onClose, onConfirm, loading, preview }) => {
    if (!open || !preview) return null;
    const renderList = (items, tone) => (
        <ul className="mt-1 flex flex-wrap gap-1">
            {items.map((s, i) => (
                <li key={i} className={`px-2 py-0.5 rounded-full text-[11px] border ${tone === "add" ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-700 dark:text-emerald-400" : "border-border text-muted-foreground"}`}>
                    {tone === "add" && "+ "}{s}
                </li>
            ))}
        </ul>
    );
    return (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose} data-testid="coach-import-diff-modal">
            <div className="bg-background rounded-2xl max-w-3xl w-full p-6 max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center justify-between mb-4">
                    <h3 className="font-heading font-semibold text-lg text-foreground">Confirm changes to your Passport</h3>
                    <button onClick={onClose} data-testid="coach-import-diff-close"><X size={16} /></button>
                </div>
                {!preview.changes?.length ? (
                    <div className="text-sm text-muted-foreground">Nothing to change — your Passport already matches these suggestions.</div>
                ) : (
                    <>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div className="card-soft p-4" data-testid="coach-diff-before">
                                <div className="overline mb-2">Before</div>
                                <div className="text-xs text-muted-foreground uppercase">Summary</div>
                                <p className="mt-1 text-sm text-muted-foreground whitespace-pre-wrap min-h-[3em]">{preview.before?.summary || "— empty —"}</p>
                                <div className="mt-3 text-xs text-muted-foreground uppercase">Skills</div>
                                {preview.before?.skills?.length ? renderList(preview.before.skills, "same") : <div className="text-sm text-muted-foreground">— empty —</div>}
                            </div>
                            <div className="card-soft p-4 border border-emerald-500/40" data-testid="coach-diff-after">
                                <div className="overline mb-2 text-emerald-700 dark:text-emerald-400">After</div>
                                <div className="text-xs text-muted-foreground uppercase">Summary</div>
                                <p className="mt-1 text-sm text-foreground whitespace-pre-wrap min-h-[3em]">{preview.after?.summary || "— empty —"}</p>
                                <div className="mt-3 text-xs text-muted-foreground uppercase">Skills</div>
                                <ul className="mt-1 flex flex-wrap gap-1">
                                    {(preview.after?.skills || []).map((s, i) => {
                                        const isNew = preview.added_skills?.includes(s);
                                        return (
                                            <li key={i} className={`px-2 py-0.5 rounded-full text-[11px] border ${isNew ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 font-semibold" : "border-border text-muted-foreground"}`}
                                                data-testid={isNew ? `coach-diff-skill-added-${i}` : undefined}>
                                                {isNew && "+ "}{s}
                                            </li>
                                        );
                                    })}
                                </ul>
                            </div>
                        </div>
                        <div className="mt-4 text-xs text-muted-foreground">
                            {preview.has_existing_passport
                                ? "Nothing is written until you confirm. Existing content is preserved unless replaced above."
                                : "This will create your Passport with the content shown on the right."}
                        </div>
                    </>
                )}
                <div className="mt-5 flex items-center justify-end gap-2">
                    <button onClick={onClose} className="btn-outline text-sm" data-testid="coach-import-diff-cancel">Cancel</button>
                    <button onClick={onConfirm} disabled={loading || !preview.changes?.length}
                        className="btn-green text-sm inline-flex items-center gap-2 disabled:opacity-60"
                        data-testid="coach-import-diff-apply">
                        {loading ? <><Loader2 size={13} className="animate-spin" /> Applying…</> : <><Sparkles size={13} /> Apply changes</>}
                    </button>
                </div>
            </div>
        </div>
    );
};

// ============================================================
// Resume feedback tool
// ============================================================
const ResumeFeedbackTool = () => {
    const ctx = useCoachContext();
    const [resumeText, setResumeText] = useState("");
    const [targetRole, setTargetRole] = useState("");
    const [roleTouched, setRoleTouched] = useState(false);
    const [busy, setBusy] = useState(false);
    const [result, setResult] = useState(null);
    const [importSel, setImportSel] = useState({ headline: true, summary: true, skills: {} });
    const [importing, setImporting] = useState(false);
    const [imported, setImported] = useState(false);
    // iter 145 — diff modal state.
    const [diffOpen, setDiffOpen] = useState(false);
    const [diffPreview, setDiffPreview] = useState(null);
    const [previewLoading, setPreviewLoading] = useState(false);

    // Prefill role from Passport context on first load.
    useEffect(() => {
        if (ctx && ctx.target_role && !roleTouched && !targetRole) {
            setTargetRole(ctx.target_role);
        }
    }, [ctx, roleTouched, targetRole]);

    const run = async () => {
        if (resumeText.trim().length < 100) {
            toast.error("Paste at least a few paragraphs of your resume (100+ characters).");
            return;
        }
        const effectiveRole = targetRole.trim() || (ctx?.target_role || "");
        if (!effectiveRole) {
            toast.error("Add a target role — or set one on your Talent Passport.");
            return;
        }
        setBusy(true);
        try {
            const { data } = await api.post("/professional/coach/resume-feedback", {
                resume_text: resumeText,
                target_role: targetRole.trim() || null,
            });
            setResult(data);
            // Preselect all skills for import by default.
            const skillsSel = {};
            (data.import_suggestions?.skills || []).forEach((s) => { skillsSel[s] = true; });
            setImportSel({ headline: !!data.import_suggestions?.headline, summary: !!data.import_suggestions?.summary, skills: skillsSel });
            setImported(false);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setBusy(false);
        }
    };

    const reset = () => { setResult(null); setImported(false); };

    const buildPayload = () => {
        const payload = {};
        if (importSel.headline && result?.import_suggestions?.headline) payload.headline = result.import_suggestions.headline;
        if (importSel.summary && result?.import_suggestions?.summary) payload.summary = result.import_suggestions.summary;
        const chosenSkills = Object.entries(importSel.skills || {}).filter(([, v]) => v).map(([k]) => k);
        if (chosenSkills.length) payload.skills = chosenSkills;
        return payload;
    };

    const openDiff = async () => {
        const payload = buildPayload();
        if (!payload.headline && !payload.summary && !payload.skills) {
            toast.error("Pick at least one item to import.");
            return;
        }
        setPreviewLoading(true);
        try {
            const { data } = await api.post("/professional/coach/import-preview", payload);
            setDiffPreview(data);
            setDiffOpen(true);
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setPreviewLoading(false); }
    };

    const doImport = async () => {
        const payload = buildPayload();
        setImporting(true);
        try {
            const { data } = await api.post("/professional/coach/import-to-passport", payload);
            toast.success(data.message || "Imported to your Talent Passport.");
            setImported(true);
            setDiffOpen(false);
            // Tell any live ProfileStrengthCard to refresh.
            window.dispatchEvent(new Event("passport-strength-refresh"));
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setImporting(false); }
    };

    if (result) {
        return (
            <div data-testid="coach-resume-result" className="space-y-5">
                <div className="flex items-center justify-between gap-2 flex-wrap">
                    <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="font-heading font-semibold text-lg text-foreground">Resume feedback</h3>
                        {result.tailored_from_passport && <TailoredBadge role={result.effective_target_role} />}
                    </div>
                    <button onClick={reset} className="btn-outline text-xs inline-flex items-center gap-1" data-testid="coach-resume-reset">
                        <RotateCcw size={12} /> Try another
                    </button>
                </div>

                {result.clarity?.length > 0 && (
                    <section>
                        <div className="overline mb-2">Clarity</div>
                        <ul className="space-y-2" data-testid="coach-resume-clarity">
                            {result.clarity.map((c, i) => (
                                <li key={i} className="card-soft p-3 text-sm">
                                    <div className="font-medium text-foreground">{c.issue}</div>
                                    <div className="text-muted-foreground mt-1">{c.suggestion}</div>
                                </li>
                            ))}
                        </ul>
                    </section>
                )}

                {result.impact_bullets?.length > 0 && (
                    <section>
                        <div className="overline mb-2">Stronger bullets</div>
                        <ul className="space-y-2" data-testid="coach-resume-impact">
                            {result.impact_bullets.map((b, i) => (
                                <li key={i} className="card-soft p-3 text-sm">
                                    <div className="text-muted-foreground line-through">{b.before}</div>
                                    <div className="mt-1 text-foreground font-medium">→ {b.after}</div>
                                    {b.why && <div className="mt-1 text-xs text-muted-foreground">Why: {b.why}</div>}
                                </li>
                            ))}
                        </ul>
                    </section>
                )}

                {result.missing_keywords?.length > 0 && (
                    <section>
                        <div className="overline mb-2">Missing keywords for {result.effective_target_role || targetRole}</div>
                        <div className="flex flex-wrap gap-2" data-testid="coach-resume-keywords">
                            {result.missing_keywords.map((k, i) => (
                                <Badge key={i} className="border border-emerald-500/40 bg-emerald-500/10 text-emerald-700 dark:text-emerald-400">{k}</Badge>
                            ))}
                        </div>
                    </section>
                )}

                {result.quick_wins?.length > 0 && (
                    <section>
                        <div className="overline mb-2">Quick wins</div>
                        <ul className="list-disc list-inside space-y-1 text-sm text-foreground" data-testid="coach-resume-wins">
                            {result.quick_wins.map((w, i) => <li key={i}>{w}</li>)}
                        </ul>
                    </section>
                )}

                {(result.import_suggestions?.headline || result.import_suggestions?.summary || result.import_suggestions?.skills?.length > 0) && (
                    <section className="card-soft p-4 border border-emerald-500/30 bg-emerald-500/5" data-testid="coach-resume-import">
                        <div className="overline text-emerald-700 dark:text-emerald-400 mb-2">Add to your Talent Passport</div>
                        <p className="text-xs text-muted-foreground mb-3">Pick what to keep — nothing is added until you confirm.</p>
                        <div className="space-y-2">
                            {result.import_suggestions.headline && (
                                <label className="flex items-start gap-2 text-sm cursor-pointer" data-testid="coach-import-headline">
                                    <input type="checkbox" className="mt-1"
                                        checked={!!importSel.headline}
                                        onChange={(e) => setImportSel((s) => ({ ...s, headline: e.target.checked }))} />
                                    <span><span className="font-semibold">Headline:</span> {result.import_suggestions.headline}</span>
                                </label>
                            )}
                            {result.import_suggestions.summary && (
                                <label className="flex items-start gap-2 text-sm cursor-pointer" data-testid="coach-import-summary">
                                    <input type="checkbox" className="mt-1"
                                        checked={!!importSel.summary}
                                        onChange={(e) => setImportSel((s) => ({ ...s, summary: e.target.checked }))} />
                                    <span><span className="font-semibold">Summary:</span> <span className="text-muted-foreground">{result.import_suggestions.summary}</span></span>
                                </label>
                            )}
                            {result.import_suggestions.skills?.length > 0 && (
                                <div className="text-sm">
                                    <div className="font-semibold mb-1">Skills:</div>
                                    <div className="flex flex-wrap gap-2" data-testid="coach-import-skills">
                                        {result.import_suggestions.skills.map((s, i) => {
                                            const on = !!importSel.skills[s];
                                            return (
                                                <button key={i} onClick={() => setImportSel((v) => ({ ...v, skills: { ...v.skills, [s]: !on } }))}
                                                    data-testid={`coach-import-skill-${i}`}
                                                    className={`px-2 py-0.5 rounded-full text-xs border transition ${on ? "border-emerald-500 bg-emerald-500/15 text-emerald-700 dark:text-emerald-400" : "border-border text-muted-foreground hover:bg-muted/40"}`}>
                                                    {on ? "✓ " : ""}{s}
                                                </button>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}
                        </div>
                        <div className="mt-4 flex items-center gap-2 flex-wrap">
                            {imported ? (
                                <span className="text-xs inline-flex items-center gap-1 text-emerald-700 dark:text-emerald-400 font-semibold" data-testid="coach-import-done">
                                    <CheckCircle2 size={12} /> Added to your Passport.
                                </span>
                            ) : (
                                <button onClick={openDiff} disabled={previewLoading}
                                    className="btn-green text-sm inline-flex items-center gap-2 disabled:opacity-60"
                                    data-testid="coach-import-confirm">
                                    {previewLoading ? <><Loader2 size={13} className="animate-spin" /> Loading preview…</> : <><Sparkles size={13} /> Review &amp; add to Passport</>}
                                </button>
                            )}
                            <Link to="/professional/passport" className="text-xs text-emerald-700 dark:text-emerald-400 hover:underline">
                                Open Passport →
                            </Link>
                        </div>
                    </section>
                )}

                <p className="text-[11px] text-muted-foreground italic">Simonara doesn't save your pasted resume — this analysis stays in your browser session.</p>

                <ImportDiffModal open={diffOpen} onClose={() => setDiffOpen(false)}
                    onConfirm={doImport} loading={importing} preview={diffPreview} />
            </div>
        );
    }

    return (
        <div className="space-y-4" data-testid="coach-resume-form">
            <div>
                <div className="flex items-center gap-2 mb-1">
                    <Label>Target role</Label>
                    {ctx?.target_role && !roleTouched && targetRole === ctx.target_role && <TailoredBadge role={ctx.target_role} />}
                </div>
                <Input
                    value={targetRole}
                    onChange={(e) => { setTargetRole(e.target.value); setRoleTouched(true); }}
                    placeholder={ctx?.target_role ? `Prefilled from Passport — ${ctx.target_role}` : "e.g. Barista, Product Manager, Registered Nurse"}
                    data-testid="coach-resume-role"
                    maxLength={120}
                />
            </div>
            <div>
                <Label>Paste your resume text</Label>
                <Textarea
                    value={resumeText}
                    onChange={(e) => setResumeText(e.target.value)}
                    rows={12}
                    placeholder="Paste your resume content here — headings, bullets, and dates are all fine."
                    data-testid="coach-resume-text"
                    maxLength={12000}
                />
                <div className="mt-1 text-[11px] text-muted-foreground">
                    {resumeText.length}/12,000 characters — not saved to the server.
                </div>
            </div>
            <button
                onClick={run}
                disabled={busy}
                className="btn-green text-sm inline-flex items-center gap-2 disabled:opacity-60"
                data-testid="coach-resume-run"
            >
                {busy ? <><Loader2 size={14} className="animate-spin" /> Reviewing…</> : <>Get feedback <Sparkles size={13} /></>}
            </button>
        </div>
    );
};

// ============================================================
// Interview practice tool
// ============================================================
const InterviewPracticeTool = () => {
    const ctx = useCoachContext();
    const [targetRole, setTargetRole] = useState("");
    const [roleTouched, setRoleTouched] = useState(false);
    const [started, setStarted] = useState(false);
    const [history, setHistory] = useState([]); // [{question, answer, feedback}]
    const [currentQ, setCurrentQ] = useState(null); // { question, feedback, turn_hint }
    const [answer, setAnswer] = useState("");
    const [busy, setBusy] = useState(false);
    const [summary, setSummary] = useState(null);
    const [tailored, setTailored] = useState(false);
    // iter 145 — set true if the current textarea contents came from Whisper.
    const [voiceInput, setVoiceInput] = useState(false);
    // iter 146 — hold the recorded audio Blob (client-side only, never
    // persisted) so we can offer replay after the turn is submitted.
    const [pendingAudio, setPendingAudio] = useState(null);
    // iter 149 — per-session roadmap step completion. Persists in
    // localStorage so a refresh keeps the strike-through, and resets
    // whenever the user starts a new practice (per user's spec:
    // "tied to latest interview session only, resets each new practice").
    const ROADMAP_STORAGE_KEY = "simonara.coach.interview.roadmap.completed";
    const [roadmapDone, setRoadmapDone] = useState(() => {
        try {
            const raw = localStorage.getItem(ROADMAP_STORAGE_KEY);
            return raw ? JSON.parse(raw) : {};
        } catch { return {}; }
    });
    useEffect(() => {
        try { localStorage.setItem(ROADMAP_STORAGE_KEY, JSON.stringify(roadmapDone)); } catch { /* quota — ignore */ }
    }, [roadmapDone]);
    const toggleRoadmap = (action) => setRoadmapDone((prev) => ({ ...prev, [action]: !prev[action] }));

    useEffect(() => {
        if (ctx && ctx.target_role && !roleTouched && !targetRole) setTargetRole(ctx.target_role);
    }, [ctx, roleTouched, targetRole]);

    const startPractice = async () => {
        const effective = targetRole.trim() || ctx?.target_role || "";
        if (!effective) {
            toast.error("Add a target role — or set one on your Talent Passport.");
            return;
        }
        setBusy(true);
        // iter 149 — reset last-practice roadmap progress at the start of
        // a fresh interview so completed strikethroughs don't carry over.
        setRoadmapDone({});
        try {
            const { data } = await api.post("/professional/coach/interview-practice", {
                target_role: targetRole.trim() || null,
                history: [],
                answer: null,
                end: false,
            });
            setCurrentQ({ question: data.question, feedback: null, turn_hint: data.turn_hint });
            setStarted(true);
            setTailored(!!data.tailored_from_passport);
            if (!targetRole.trim() && data.effective_target_role) setTargetRole(data.effective_target_role);
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setBusy(false); }
    };

    const submitAnswer = async () => {
        if (!answer.trim()) {
            toast.error("Type an answer before submitting.");
            return;
        }
        setBusy(true);
        try {
            const nextHistory = [...history, { question: currentQ.question, answer, feedback: null, naturalness_score: null, audio: pendingAudio }];
            const { data } = await api.post("/professional/coach/interview-practice", {
                target_role: targetRole,
                history: nextHistory.map((h) => ({ question: h.question, answer: h.answer, feedback: h.feedback })),
                answer: null,
                end: false,
                voice_input: voiceInput,
            });
            // Patch last item with the feedback + naturalness score from this turn.
            if (data.feedback) nextHistory[nextHistory.length - 1].feedback = data.feedback;
            if (data.naturalness_score != null) nextHistory[nextHistory.length - 1].naturalness_score = data.naturalness_score;
            setHistory(nextHistory);
            setAnswer("");
            setVoiceInput(false);
            setPendingAudio(null);
            setCurrentQ({ question: data.question, feedback: null, turn_hint: data.turn_hint });
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setBusy(false); }
    };

    const finishPractice = async () => {
        setBusy(true);
        try {
            const finalHistory = [...history];
            if (answer.trim() && currentQ) {
                finalHistory.push({ question: currentQ.question, answer, feedback: null });
            }
            const { data } = await api.post("/professional/coach/interview-practice", {
                target_role: targetRole,
                history: finalHistory.map((h) => ({ question: h.question, answer: h.answer, feedback: h.feedback })),
                answer: null,
                end: true,
            });
            setSummary(data);
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setBusy(false); }
    };

    const reset = () => {
        setStarted(false); setHistory([]); setCurrentQ(null); setAnswer(""); setSummary(null);
    };

    if (summary) {
        return (
            <div className="space-y-5" data-testid="coach-interview-summary">
                <div className="flex items-center justify-between gap-2 flex-wrap">
                    <h3 className="font-heading font-semibold text-lg text-foreground">Interview summary</h3>
                    <button onClick={reset} className="btn-outline text-xs inline-flex items-center gap-1" data-testid="coach-interview-reset">
                        <RotateCcw size={12} /> New practice
                    </button>
                </div>
                <p className="text-sm text-foreground">{summary.summary}</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div className="card-soft p-4">
                        <div className="overline text-emerald-700 dark:text-emerald-400 mb-2">Strengths</div>
                        <ul className="list-disc list-inside space-y-1 text-sm text-foreground">
                            {summary.strengths?.map((s, i) => <li key={i}>{s}</li>)}
                        </ul>
                    </div>
                    <div className="card-soft p-4">
                        <div className="overline text-amber-700 dark:text-amber-400 mb-2">Sharpen these</div>
                        <ul className="list-disc list-inside space-y-1 text-sm text-foreground">
                            {summary.improve?.map((s, i) => <li key={i}>{s}</li>)}
                        </ul>
                    </div>
                </div>
                <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/5 p-4">
                    <div className="text-sm text-foreground font-medium">{summary.next_step}</div>
                    <div className="mt-1 text-sm text-muted-foreground">{summary.cta.hint}</div>
                    <CoachCTA cta={summary.cta} />
                </div>

                {summary.roadmap?.length > 0 && (
                    <div data-testid="coach-interview-roadmap">
                        <div className="flex items-center justify-between gap-2 mb-2">
                            <div className="overline">Your 3-step follow-up plan</div>
                            <span className="text-[11px] text-emerald-700 dark:text-emerald-400 font-semibold" data-testid="coach-roadmap-progress">
                                {summary.roadmap.filter((s) => roadmapDone[s.action]).length}/{summary.roadmap.length} done
                            </span>
                        </div>
                        <ol className="space-y-2">
                            {summary.roadmap.map((step, i) => {
                                // Fixed action → deep-link mapping. Do not
                                // let the LLM invent destinations.
                                const to = step.action === "video_intro"
                                    ? "/professional/passport?section=video"
                                    : step.action === "practice_role"
                                    ? "/professional/coach-tools?tool=interview"
                                    : "/professional/passport";
                                const label = step.action === "video_intro"
                                    ? "Open Passport"
                                    : step.action === "practice_role"
                                    ? "Start a new practice"
                                    : "Publish my Passport";
                                const done = !!roadmapDone[step.action];
                                return (
                                    <li key={i} className={`card-soft p-3 flex items-start gap-3 transition ${done ? "opacity-60" : ""}`} data-testid={`coach-roadmap-step-${i}`}>
                                        <button
                                            type="button"
                                            onClick={() => toggleRoadmap(step.action)}
                                            className={`mt-0.5 flex-none inline-flex items-center justify-center w-6 h-6 rounded-full border transition ${done ? "bg-emerald-500 border-emerald-500 text-white" : "bg-emerald-500/15 border-emerald-500/40 text-emerald-700 dark:text-emerald-400 hover:bg-emerald-500/25"}`}
                                            aria-label={done ? `Mark step ${i + 1} as not done` : `Mark step ${i + 1} as done`}
                                            data-testid={`coach-roadmap-toggle-${step.action}`}
                                        >
                                            {done ? <CheckCircle2 size={13} /> : <span className="text-xs font-bold">{i + 1}</span>}
                                        </button>
                                        <div className="flex-1 min-w-0">
                                            <div className={`text-sm font-semibold text-foreground ${done ? "line-through text-muted-foreground" : ""}`} data-testid={`coach-roadmap-title-${step.action}`}>
                                                {step.title}
                                            </div>
                                            <div className={`mt-0.5 text-xs text-muted-foreground ${done ? "line-through" : ""}`}>{step.detail}</div>
                                            {!done && (
                                                <Link to={to} className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-emerald-600 hover:underline"
                                                    data-testid={`coach-roadmap-cta-${step.action}`}>
                                                    {label} <ArrowRight size={12} />
                                                </Link>
                                            )}
                                        </div>
                                    </li>
                                );
                            })}
                        </ol>
                    </div>
                )}
            </div>
        );
    }

    if (!started) {
        return (
            <div className="space-y-4" data-testid="coach-interview-form">
                <div>
                    <div className="flex items-center gap-2 mb-1">
                        <Label>Target role</Label>
                        {ctx?.target_role && !roleTouched && targetRole === ctx.target_role && <TailoredBadge role={ctx.target_role} />}
                    </div>
                    <Input
                        value={targetRole}
                        onChange={(e) => { setTargetRole(e.target.value); setRoleTouched(true); }}
                        placeholder={ctx?.target_role ? `Prefilled from Passport — ${ctx.target_role}` : "e.g. Barista, Product Manager, Registered Nurse"}
                        data-testid="coach-interview-role"
                        maxLength={120}
                    />
                </div>
                <button
                    onClick={startPractice}
                    disabled={busy}
                    className="btn-green text-sm inline-flex items-center gap-2 disabled:opacity-60"
                    data-testid="coach-interview-start"
                >
                    {busy ? <><Loader2 size={14} className="animate-spin" /> Starting…</> : <>Start practice <MessageSquare size={13} /></>}
                </button>
                <p className="text-[11px] text-muted-foreground italic">
                    Your answers stay in this session only — nothing is saved unless you add it to your Passport.
                </p>
            </div>
        );
    }

    return (
        <div className="space-y-4" data-testid="coach-interview-live">
            <div className="text-xs text-muted-foreground flex items-center gap-2 flex-wrap">
                Question {history.length + 1} · {targetRole}
                {tailored && <TailoredBadge role={targetRole} />}
            </div>
            {history.map((h, i) => (
                <div key={i} className="card-soft p-4">
                    <div className="text-sm font-semibold text-foreground">Q{i + 1}. {h.question}</div>
                    {!h.audio && (
                        <div className="mt-2 text-sm text-muted-foreground whitespace-pre-wrap"><span className="font-semibold text-foreground">You:</span> {h.answer}</div>
                    )}
                    {h.feedback && !h.audio && (
                        <div className="mt-2 text-sm text-foreground border-l-2 border-emerald-500 pl-3">
                            <Lightbulb size={12} className="inline text-emerald-500 mr-1" /> {h.feedback}
                        </div>
                    )}
                    {h.audio && (
                        <VoicePlayback blob={h.audio} transcript={h.answer} feedback={h.feedback} />
                    )}
                    {h.naturalness_score != null && (
                        <div className="mt-2 text-[11px] text-emerald-700 dark:text-emerald-400 font-semibold" data-testid={`coach-interview-naturalness-${i}`}>
                            Naturalness score: {h.naturalness_score}/100
                        </div>
                    )}
                </div>
            ))}
            {currentQ && (
                <motion.div
                    initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
                    className="card-soft p-4 border border-emerald-500/30"
                    data-testid="coach-interview-current-q"
                >
                    <div className="text-sm font-semibold text-foreground">Q{history.length + 1}. {currentQ.question}</div>
                    {currentQ.turn_hint && (
                        <div className="mt-1 text-[11px] text-emerald-700 dark:text-emerald-400">Tip: {currentQ.turn_hint}</div>
                    )}
                    <Textarea
                        value={answer}
                        onChange={(e) => { setAnswer(e.target.value); setVoiceInput(false); setPendingAudio(null); }}
                        rows={5}
                        placeholder="Type your answer…"
                        className="mt-3"
                        data-testid="coach-interview-answer"
                        maxLength={4000}
                    />
                    <div className="mt-2 flex items-center gap-2 flex-wrap">
                        <MicRecorder
                            disabled={busy}
                            onTranscript={(t, blob) => { setAnswer(t); setVoiceInput(true); setPendingAudio(blob || null); }}
                        />
                        {voiceInput && (
                            <span className="text-[11px] text-emerald-700 dark:text-emerald-400" data-testid="coach-interview-voice-flag">
                                Transcribed from voice — you can edit before submitting.
                            </span>
                        )}
                    </div>
                    <div className="mt-3 flex items-center gap-2 flex-wrap">
                        <button
                            onClick={submitAnswer}
                            disabled={busy}
                            className="btn-green text-sm inline-flex items-center gap-2 disabled:opacity-60"
                            data-testid="coach-interview-submit"
                        >
                            {busy ? <><Loader2 size={14} className="animate-spin" /> Sending…</> : <>Next question <Send size={13} /></>}
                        </button>
                        <button
                            onClick={finishPractice}
                            disabled={busy || history.length < 1}
                            className="btn-outline text-sm inline-flex items-center gap-2 disabled:opacity-60"
                            data-testid="coach-interview-finish"
                        >
                            Wrap up
                        </button>
                    </div>
                </motion.div>
            )}
        </div>
    );
};

// ============================================================
// Salary negotiation tips tool
// ============================================================
const SalaryTipsTool = () => {
    const ctx = useCoachContext();
    const [targetRole, setTargetRole] = useState("");
    const [roleTouched, setRoleTouched] = useState(false);
    const [location, setLocation] = useState("");
    const [level, setLevel] = useState("");
    const [busy, setBusy] = useState(false);
    const [result, setResult] = useState(null);

    useEffect(() => {
        if (ctx && ctx.target_role && !roleTouched && !targetRole) setTargetRole(ctx.target_role);
    }, [ctx, roleTouched, targetRole]);

    const run = async () => {
        const effective = targetRole.trim() || ctx?.target_role || "";
        if (!effective) {
            toast.error("Add a target role — or set one on your Talent Passport.");
            return;
        }
        setBusy(true);
        try {
            const { data } = await api.post("/professional/coach/salary-tips", {
                target_role: targetRole.trim() || null,
                location: location || null,
                experience_level: level || null,
            });
            setResult(data);
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setBusy(false); }
    };

    const reset = () => { setResult(null); };

    if (result) {
        return (
            <div className="space-y-5" data-testid="coach-salary-result">
                <div className="flex items-center justify-between gap-2 flex-wrap">
                    <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="font-heading font-semibold text-lg text-foreground">Negotiation framework</h3>
                        {result.tailored_from_passport && <TailoredBadge role={result.effective_target_role} />}
                    </div>
                    <button onClick={reset} className="btn-outline text-xs inline-flex items-center gap-1" data-testid="coach-salary-reset">
                        <RotateCcw size={12} /> Try another
                    </button>
                </div>

                {result.research_sources?.length > 0 && (
                    <section>
                        <div className="overline mb-2">Where to research the range</div>
                        <ul className="space-y-2" data-testid="coach-salary-sources">
                            {result.research_sources.map((s, i) => (
                                <li key={i} className="card-soft p-3 text-sm">
                                    <div className="font-medium text-foreground inline-flex items-center gap-1">
                                        <ExternalLink size={12} className="text-emerald-500" /> {s.name}
                                    </div>
                                    <div className="text-muted-foreground mt-1">{s.why}</div>
                                </li>
                            ))}
                        </ul>
                    </section>
                )}

                {result.anchor_framework?.length > 0 && (
                    <section>
                        <div className="overline mb-2">How to anchor the conversation</div>
                        <ul className="list-decimal list-inside space-y-1 text-sm text-foreground" data-testid="coach-salary-anchor">
                            {result.anchor_framework.map((a, i) => <li key={i}>{a}</li>)}
                        </ul>
                    </section>
                )}

                {result.pitfalls?.length > 0 && (
                    <section>
                        <div className="overline mb-2">What to avoid</div>
                        <ul className="list-disc list-inside space-y-1 text-sm text-foreground" data-testid="coach-salary-pitfalls">
                            {result.pitfalls.map((p, i) => <li key={i}>{p}</li>)}
                        </ul>
                    </section>
                )}

                {result.scripts?.length > 0 && (
                    <section>
                        <div className="overline mb-2">What to say</div>
                        <ul className="space-y-2" data-testid="coach-salary-scripts">
                            {result.scripts.map((s, i) => (
                                <li key={i} className="card-soft p-3 text-sm">
                                    <div className="text-muted-foreground italic">When: {s.situation}</div>
                                    <div className="mt-1 text-foreground">"{s.say_this}"</div>
                                </li>
                            ))}
                        </ul>
                    </section>
                )}

                <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/5 p-4">
                    <div className="text-sm text-muted-foreground">{result.cta.hint}</div>
                    <CoachCTA cta={result.cta} />
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-4" data-testid="coach-salary-form">
            <div>
                <div className="flex items-center gap-2 mb-1">
                    <Label>Target role</Label>
                    {ctx?.target_role && !roleTouched && targetRole === ctx.target_role && <TailoredBadge role={ctx.target_role} />}
                </div>
                <Input
                    value={targetRole}
                    onChange={(e) => { setTargetRole(e.target.value); setRoleTouched(true); }}
                    placeholder={ctx?.target_role ? `Prefilled from Passport — ${ctx.target_role}` : "e.g. Barista, Product Manager, Registered Nurse"}
                    data-testid="coach-salary-role"
                    maxLength={120}
                />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                    <Label>Location (optional)</Label>
                    <Input
                        value={location}
                        onChange={(e) => setLocation(e.target.value)}
                        placeholder="e.g. Los Angeles, CA"
                        data-testid="coach-salary-location"
                        maxLength={120}
                    />
                </div>
                <div>
                    <Label>Experience level (optional)</Label>
                    <Input
                        value={level}
                        onChange={(e) => setLevel(e.target.value)}
                        placeholder="e.g. Entry-level, Mid, Senior"
                        data-testid="coach-salary-level"
                        maxLength={60}
                    />
                </div>
            </div>
            <button
                onClick={run}
                disabled={busy}
                className="btn-green text-sm inline-flex items-center gap-2 disabled:opacity-60"
                data-testid="coach-salary-run"
            >
                {busy ? <><Loader2 size={14} className="animate-spin" /> Preparing…</> : <>Get tips <Sparkles size={13} /></>}
            </button>
        </div>
    );
};

// ============================================================
// Page shell + tab switcher
// ============================================================
const CoachTools = () => {
    const [params, setParams] = useSearchParams();
    const initialTool = TOOLS.some((t) => t.key === params.get("tool")) ? params.get("tool") : "resume";
    const [active, setActive] = useState(initialTool);

    const setTool = useCallback((key) => {
        setActive(key);
        setParams({ tool: key }, { replace: true });
    }, [setParams]);

    useEffect(() => {
        const t = params.get("tool");
        if (t && TOOLS.some((x) => x.key === t) && t !== active) setActive(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [params]);

    return (
        <div data-testid="coach-tools-page">
            <div className="mb-6">
                <p className="overline">Simonara Coach</p>
                <h1 data-testid="dash-page-title" className="mt-1 font-heading font-medium text-2xl sm:text-3xl tracking-tight text-foreground">
                    Career Coach
                </h1>
                <p className="mt-1 text-sm text-muted-foreground max-w-2xl">
                    Free AI tools to sharpen your resume, practice interviews, and prepare
                    for salary conversations. Every tool sends you back to your Talent
                    Passport with something new to add.
                </p>
            </div>

            {/* Tab bar */}
            <div className="mb-6 grid grid-cols-3 gap-2" data-testid="coach-tabs">
                {TOOLS.map((t) => {
                    const Icon = t.icon;
                    const isActive = active === t.key;
                    return (
                        <button
                            key={t.key}
                            onClick={() => setTool(t.key)}
                            data-testid={`coach-tab-${t.key}`}
                            className={`text-left rounded-xl border p-3 sm:p-4 transition ${
                                isActive
                                    ? "border-emerald-500 bg-emerald-500/10"
                                    : "border-border hover:bg-muted/40"
                            }`}
                        >
                            <div className="flex items-center gap-2">
                                <Icon size={16} className={isActive ? "text-emerald-600" : "text-muted-foreground"} />
                                <div className="font-heading font-semibold text-sm text-foreground">{t.label}</div>
                            </div>
                            <div className="mt-1 text-[11px] text-muted-foreground hidden sm:block">{t.desc}</div>
                        </button>
                    );
                })}
            </div>

            <div className="card-soft p-5 sm:p-6">
                {active === "resume" && <ResumeFeedbackTool />}
                {active === "interview" && <InterviewPracticeTool />}
                {active === "salary" && <SalaryTipsTool />}
            </div>
        </div>
    );
};

export default CoachTools;
